import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { MaintenancesApi } from '@api/maintenances/maintenances.api';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Maintenance } from '@core/interfaces';
import { LocationSector } from '@core/interfaces/locations-sectors';
import { DialogEditMaintenanceComponent } from 'app/main/maintenances/dialogs/dialog-edit-maintenance/dialog-maintenances.component';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { TabMaintenancesService } from './services/tab-maintenances.service';

@Component({
  selector: 'tab-maintenances',
  templateUrl: './tab-maintenances.component.html',
  styleUrls: ['./tab-maintenances.component.scss']
})
export class TabMaintenancesComponent implements OnInit, OnDestroy {
  @ViewChild('tabElement', { static: true })
  public tabElement: ElementRef<HTMLDivElement>;

  @Input()
  public locationSector: LocationSector;

  public cursor = null;
  public isLoading = true;
  public hasMoreData = true;
  public isMaintenanceViewMode = false;

  public maintenances: Partial<Maintenance>[] = [];
  public loadMore$: Subject<void> = new Subject<void>();
  public subscription$: Subject<void> = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly matDialog: MatDialog,
    private readonly maintenancesApi: MaintenancesApi,
    private readonly authenticationService: AuthenticationService,
    private readonly tabMaintenancesService: TabMaintenancesService
  ) {
    this.isMaintenanceViewMode = !this.authenticationService.hasPermission('maintenances-edit');
  }

  public ngOnInit(): void {
    this.loadMore$.pipe(
      takeUntil(this.subscription$),
      tap(() => this.isLoading = true),
      switchMap(() => this.tabMaintenancesService.getSectorMaintenances({
        id: this.locationSector.id,
        take: 100,
        after: this.cursor
      }))
    ).subscribe((maintenances) => {
      this.cursor = maintenances.pageInfo.endCursor;
      this.maintenances = this.maintenances.concat(maintenances.items);
      this.hasMoreData = maintenances.pageInfo.startCursor !== maintenances.pageInfo.endCursor;
      this.isLoading = false;
    });

    this.loadMore$.next();
  }

  public ngOnDestroy(): void {
    this.subscription$.next();
    this.subscription$.complete();
  }

  public toMaintenancesPage(): void {
    const queryParams = {
      locationId_in: this.locationSector.location.id,
      sectorId: this.locationSector.id,
    };

    this.router.navigate(['/manutencoes'], { queryParams });
  }

  public openMaintenanceDialog(maintenanceId: string): void {
    this.maintenancesApi.searchOne(maintenanceId).subscribe((maintenance) => {
      this.matDialog.open(DialogEditMaintenanceComponent, {
        panelClass: 'dialog-edit-maintenance',
        data: {
          maintenance,
          isViewMode: this.isMaintenanceViewMode
        }
      });
    });
  }


  @HostListener('scroll', ['$event.target'])
  public onScroll(): void {
    const { scrollTop, scrollHeight, clientHeight } = this.tabElement.nativeElement;
    const isElementAtBottom = scrollTop + clientHeight === scrollHeight;
    if (isElementAtBottom && !this.isLoading && this.hasMoreData) {
      this.loadMore$.next();
    }
  }
}
